import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MatterGeneralCard from './matterGeneralCard';
import EntriesView from './entries/entriesView';
import PartiesListing from '../parties/partiesListing';
import IdentificationListing from '../identifications/indentificationListing';
import { useLocation, useParams } from 'react-router-dom';
import { IMatter, MatterSections, MatterTabSections } from '../../interface/matter';
import { useFetchMatterById } from '../../hooks/useFetchMatterById';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import Notes from '../conflictSearch/notes';
import { NotesEntity } from '../../constants/notes';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { EntryEntity } from '../../constants/entries';
import { useUser } from '../../context/UserContext';
import { UserType } from '../../constants/constants';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomTabs from '../customTabs/customTabs';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';

const MatterDetailView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id: matterId } = useParams();
  const { user } = useUser();
  const { setTitle } = usePage();
  const { goBack, updateTop } = useNavigationStack();
  const { trackPage } = useAnalytics();
  const fromTab = location?.state?.fromTab ?? MatterSections.general;

  const [selectedTab, setSelectedTab] = useState<string>(fromTab.toString());

  useEffect(() => {
    updateTop({ fromTab: selectedTab });
  }, [selectedTab]);

  const {
    data: matter,
    isLoading,
    refetch,
  } = useFetchMatterById({
    id: matterId,
    withFavoriteInformation: true,
    withDefaultTransactionType: true,
    withShowOnlyOwnEntries: true,
    withHasLinkedTransactionTypes: true,
    withHasSubMatters: true,
    relations: [
      'ownerUser:fullName',
      'responsibleUser:fullName',
      'matterType:translations',
      'businessUnit:name',
      'businessArea:translations',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
    ],
    fields: [
      'subject',
      'matterNumber',
      'status',
      'invoiceableType',
      'openingDate',
      'language',
      'currency',
      'conflictCheckDoneDate',
      'hasImportantNotes',
      'hasImportantRelatedNotes',
      'hasNotes',
      'lastEntryDate',
      'fixedPrice',
      'fixedPriceInUse',
      'parentMatterId',
      'insider',
      'language',
    ],
  });

  useEffect(() => {
    trackPage(AnalyticsPage.MATTER_DETAIL);
  }, [trackPage]);

  const handleBack = () => {
    goBack({ fromTab: MatterTabSections.matters });
  };
  useEffect(() => {
    if (matter && !isLoading) {
      setTitle(
        <AddEditTransactionHeader
          title={matter.subject}
          subTitle={t('common:features.matters')}
          handleBack={handleBack}
        />,
      );
    }
  }, [matter, isLoading]);

  const items = useMemo(
    () => [
      {
        key: MatterSections.general.toString(),
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <MatterGeneralCard matter={matter as IMatter} refetch={refetch} isLoading={isLoading} />
          </DetailScrollArea>
        ),
      },
      {
        key: MatterSections.entries.toString(),
        label: t('common:tab.entries'),
        children: <EntriesView entity={EntryEntity.MATTER} matter={matter} />,
      },
      ...(user?.userType !== UserType.RESTRICTED
        ? [
            {
              key: MatterSections.parties.toString(),
              label: t('common:tab.parties'),
              children: <PartiesListing type="matter" />,
            },
            {
              key: MatterSections.identification.toString(),
              label: t('common:tab.identifications'),
              children: <IdentificationListing type="matter" />,
            },
            {
              key: MatterSections.notes.toString(),
              label: t('common:tab.notes'),
              children: <Notes entity={NotesEntity.MATTER} entitySubject={matter?.subject ?? ''} />,
            },
          ]
        : []),
    ],
    [t, matter, isLoading],
  );
  return (
    <CustomTabs variant="pill" activeKey={selectedTab} onChange={setSelectedTab} items={items} />
  );
};

export default MatterDetailView;
